import { useQuery } from '@apollo/react-hooks';
import { useState, useEffect, useMemo } from 'react';
import GetCustomersV3 from '../../../gql/get-customers-v3';

const useCustomerTable = (accountId, searchTerm = '', segmentFilter = '') => {
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({
    segment: '',
    minValue: null,
    maxValue: null,
    attendanceRate: '',
    locationCategory: '',
    dateFrom: '',
    dateTo: '',
  });

  const itemsPerPage = 15;

  // Build input variables for the query
  const queryVariables = useMemo(() => {
    const filters = {};

    // Only add non-empty filters
    if (searchTerm) filters.search = searchTerm;
    if (segmentFilter) filters.segment = segmentFilter;
    if (appliedFilters.segment && !segmentFilter)
      filters.segment = appliedFilters.segment;
    if (appliedFilters.minValue)
      filters.minValue = parseFloat(appliedFilters.minValue);
    if (appliedFilters.maxValue)
      filters.maxValue = parseFloat(appliedFilters.maxValue);
    if (appliedFilters.attendanceRate)
      filters.attendanceRate = appliedFilters.attendanceRate;
    if (appliedFilters.locationCategory)
      filters.locationCategory = appliedFilters.locationCategory;
    if (appliedFilters.dateFrom) filters.dateFrom = appliedFilters.dateFrom;
    if (appliedFilters.dateTo) filters.dateTo = appliedFilters.dateTo;

    return {
      input: {
        account_id: accountId ? parseInt(accountId) : 0,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
      },
    };
  }, [accountId, currentPage, searchTerm, segmentFilter, appliedFilters]);

  // Execute the query
  const { data, loading, error, refetch } = useQuery(GetCustomersV3, {
    variables: queryVariables,
    skip: !accountId || isNaN(parseInt(accountId)),
    fetchPolicy: 'cache-and-network',
  });

  // Transform the data to match the component's expected format
  const customers = useMemo(() => {
    if (!data?.getCustomersV3?.results) return [];

    return data.getCustomersV3.results.map((customer) => ({
      id: customer.id,
      user_id: customer.user_id, // Add user_id field
      name: `${customer.last_name}, ${customer.first_name}`,
      email: customer.email,
      phone: customer.cellphone,
      since: customer.customer_since,
      lastPurchase: customer.last_purchase_date,
      segment: mapSegmentToDisplay(customer.segment),
      segment_direction: customer.segment_direction, // Add segment_direction field
      has_season_pass: customer.has_season_pass, // Add has_season_pass field
      lifetimeValue: customer.lifetime_value,
      avgPerEvent: customer.average_per_event,
      eventsAttended: customer.events_attended,
      totalEvents: customer.total_events,
      attendance: customer.attendance_rate,
      distance: customer.distance_miles,
      locationCategory: customer.location_category,
      // Keep raw values for filtering
      rawSegment: customer.segment,
    }));
  }, [data]);

  const totalCount = data?.getCustomersV3?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Helper function to map segment values to display names
  function mapSegmentToDisplay(segment) {
    const segmentMap = {
      season_pass: 'Season Pass',
      super_fan: 'Super Fan',
      regular_fan: 'Regular Fan',
      casual_fan: 'Casual Fan',
      occasional: 'Occasional',
      first_timer: 'First Timer',
      inactive: 'Inactive',
    };
    return segmentMap[segment] || segment;
  }

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, segmentFilter, appliedFilters]);

  return {
    customers,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    itemsPerPage,
    appliedFilters,
    setAppliedFilters,
    refetch,
  };
};

export default useCustomerTable;
